import { useEffect, useState } from 'react';
import type { UserContextApi } from '@/types/api/user-context';
import type { WootricUser } from '@/types/wootric';

interface UseUserProfileOptions {
  userContext: UserContextApi | null;
  isLoading: boolean;
}

interface UseUserProfileResult {
  user: WootricUser | null;
  isLoading: boolean;
  error: Error | null;
}

export function useUserProfile(options: UseUserProfileOptions): UseUserProfileResult {
  const { userContext, isLoading: contextLoading } = options;
  const [user, setUser] = useState<WootricUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (contextLoading) {
      setIsLoading(true);
      return;
    }

    try {
      if (!userContext) {
        setIsLoading(false);
        return;
      }

      if (!userContext.email) {
        setIsLoading(false);
        return;
      }

      setUser({
        email: userContext.email,
        userId: userContext.userId,
        role: userContext.role || undefined,
        createdAt: userContext.createdAt ? new Date(userContext.createdAt) : undefined,
        customerName: userContext.evoOrganisationName || userContext.evoOrganisationId || undefined,
      });
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to get user profile'));
    } finally {
      setIsLoading(false);
    }
  }, [userContext, contextLoading]);

  return { user, isLoading, error };
}
